import React from 'react';
import moment from 'moment';
import { Input, Button, Select, DatePicker, Checkbox, Textarea } from 'components';
import { UserService } from 'services';
import { Globals, Constants, checkPermission } from 'utils';
import { Menu, TrashIcon } from 'assets/icons';

interface Props {
	onClose?: any;
	item?: any;
	onDelete?: any;
	plan_free?: any;
}

// TODO: en el futuro cargar planes desde PlanService.admin.get()
const AVAILABLE_PLANS = [
	{ id: 2, name: 'Plan Premium' }
];

const LIFETIME_DATE = '2099-12-31';
const DATE_FORMAT = 'YYYY-MM-DD';

const DURATION_CHIPS: Array<{ key: string; label: string; compute: () => string }> = [
	{ key: '1m',   label: '1 mes',    compute: () => moment().add(1, 'month').format(DATE_FORMAT) },
	{ key: '6m',   label: '6 meses',  compute: () => moment().add(6, 'months').format(DATE_FORMAT) },
	{ key: '1y',   label: '1 año',    compute: () => moment().add(1, 'year').format(DATE_FORMAT) },
	{ key: '5y',   label: '5 años',   compute: () => moment().add(5, 'years').format(DATE_FORMAT) },
	{ key: 'life', label: 'Vitalicio', compute: () => LIFETIME_DATE }
];

interface State {
	form: {
		name: string;
		lastname: string;
		email: string;
	};
	currentPlan: any;
	loadingPlan: boolean;
	showAssignSection: boolean;
	assignForm: {
		plan_id: any;
		expiration_date: string;
		is_complimentary: boolean;
		notes: string;
	};
	selectedChip: string;
	assigning: boolean;
	newPassword: string;
	showPassword: boolean;
	passwordSaving: boolean;
}

class ModalUpdate extends React.Component<Props, State> {

	state: State = {
		form: {
			name: '',
			lastname: '',
			email: ''
		},
		currentPlan: null,
		loadingPlan: false,
		showAssignSection: false,
		assignForm: {
			plan_id: AVAILABLE_PLANS[0]?.id ? String(AVAILABLE_PLANS[0].id) : '',
			expiration_date: '',
			is_complimentary: true,
			notes: ''
		},
		selectedChip: '',
		assigning: false,
		newPassword: '',
		showPassword: false,
		passwordSaving: false
	}

	componentDidMount() {
		this.setState({
			form: {
				...this.state.form,
				name: this.props.item?.name || '',
				lastname: this.props.item?.lastname || '',
				email: this.props.item?.email || ''
			}
		});

		this.loadPlan();
	}

	loadPlan = async () => {
		if (!this.props.item?.id) return;
		this.setState({ loadingPlan: true });
		try {
			const res: any = await UserService.admin.getPlan({
				user_id: this.props.item.id,
				withoutLoading: true
			});
			this.setState({
				currentPlan: res?.plan_user || null,
				loadingPlan: false
			});
		} catch (e) {
			this.setState({ loadingPlan: false });
		}
	}

	submit = async (e: any) => {
		e.preventDefault();

		await UserService.admin.update({
			_user_id: this.props.item?.id,
			...this.state.form,
		});
		Globals.showSuccess("Se ha modificado el usuario correctamente");
		this.props.onClose();
	}

	change = (e: any,callback = () => {}) => {
		this.setState({
			form: {
				...this.state.form,
				[e.target.name]: e.target.value
			}
		},callback);
	}

	toggleAssignSection = () => {
		this.setState({ showAssignSection: !this.state.showAssignSection });
	}

	pickChip = (chip: { key: string; compute: () => string }) => {
		this.setState({
			selectedChip: chip.key,
			assignForm: {
				...this.state.assignForm,
				expiration_date: chip.compute()
			}
		});
	}

	changeExpirationDate = (date: any) => {
		const formatted = date ? moment(date).format(DATE_FORMAT) : '';
		this.setState({
			selectedChip: '',
			assignForm: {
				...this.state.assignForm,
				expiration_date: formatted
			}
		});
	}

	changeAssignField = (field: string, value: any) => {
		this.setState({
			assignForm: {
				...this.state.assignForm,
				[field]: value
			}
		});
	}

	confirmAssign = async () => {
		const { plan_id, expiration_date, is_complimentary, notes } = this.state.assignForm;

		if (!plan_id) {
			Globals.showError("Debe seleccionar un plan");
			return;
		}
		if (!expiration_date) {
			Globals.showError("Debe seleccionar una fecha de expiración");
			return;
		}
		if (notes && notes.length > 500) {
			Globals.showError("La nota no puede exceder 500 caracteres");
			return;
		}

		this.setState({ assigning: true });
		try {
			await UserService.admin.assignPlan({
				user_id: this.props.item?.id,
				plan_id: Number(plan_id),
				expiration_date,
				is_complimentary,
				notes
			});
			Globals.showSuccess("Plan asignado correctamente");
			this.setState({
				assigning: false,
				showAssignSection: false,
				selectedChip: '',
				assignForm: {
					plan_id: AVAILABLE_PLANS[0]?.id ? String(AVAILABLE_PLANS[0].id) : '',
					expiration_date: '',
					is_complimentary: true,
					notes: ''
				}
			});
			this.loadPlan();
		} catch (error: any) {
			this.setState({ assigning: false });
			Globals.showError(error?.message || "No se pudo asignar el plan");
		}
	}

	handleSavePassword = async () => {
		const { newPassword } = this.state;
		if (!newPassword) return;

		if (newPassword.length < 6) {
			Globals.showError('La contraseña debe tener al menos 6 caracteres');
			return;
		}

		this.setState({ passwordSaving: true });

		try {
			await UserService.admin.changePassword({
				user_id: this.props.item?.id,
				new_password: newPassword
			});
			Globals.showSuccess('Contraseña actualizada correctamente');
			this.setState({ newPassword: '', showPassword: false, passwordSaving: false });
		} catch (err: any) {
			this.setState({ passwordSaving: false });
			Globals.showError(err?.message || 'Error al cambiar contraseña');
		}
	}

	renderPasswordSection() {
		const { newPassword, showPassword, passwordSaving } = this.state;
		const tooShort = newPassword.length > 0 && newPassword.length < 6;

		return (
			<div className="section-block">
				<p className="section-title">Cambiar contraseña</p>
				<p className="password-hint">
					Si necesitas resetear el password de este usuario, ingrésalo abajo
					y presiona "Cambiar contraseña". No afecta el resto de los cambios.
				</p>

				<div className="password-field">
					<input
						type={ showPassword ? 'text' : 'password' }
						className="password-input"
						placeholder="Nueva contraseña"
						value={ newPassword }
						autoComplete="new-password"
						onChange={ (e: any) => this.setState({ newPassword: e.target.value }) } />
					<button
						type="button"
						className="password-toggle"
						aria-label={ showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña' }
						onClick={ () => this.setState({ showPassword: !showPassword }) }>
						{ showPassword ? '🙈' : '👁️' }
					</button>
				</div>

				{ tooShort ? (
					<p className="password-error">Mínimo 6 caracteres</p>
				) : (
					<p className="password-help">Mínimo 6 caracteres</p>
				) }

				<button
					type="button"
					className="btn-change-password"
					disabled={ passwordSaving || !newPassword || tooShort }
					onClick={ this.handleSavePassword }>
					{ passwordSaving ? 'Guardando...' : 'Cambiar contraseña' }
				</button>
			</div>
		);
	}

	renderCurrentPlan() {
		const plan = this.state.currentPlan;

		if (!plan) {
			return (
				<div className="plan-card">
					<div className="plan-card-row">
						<div className="plan-icon">
							<img src={ Menu.Menu3 } />
						</div>
						<div className="plan-info">
							<p className="plan-name">{ this.props.plan_free?.name || 'Plan Gratuito' }</p>
							<p className="plan-expiration">Sin fecha de expiración</p>
						</div>
					</div>
				</div>
			);
		}

		const planName = plan.plan?.name || 'Plan';
		const expirationStr = plan.expiration_date ? moment(plan.expiration_date).format(DATE_FORMAT) : '';
		const isLifetime = expirationStr === LIFETIME_DATE;
		const expirationLabel = plan.expiration_date
			? (isLifetime ? 'Vitalicio' : `Expira: ${ moment(plan.expiration_date).format('DD/MM/YYYY') }`)
			: null;

		return (
			<div className="plan-card">
				<div className="plan-card-row">
					<div className="plan-icon">
						<img src={ Menu.Menu3 } />
					</div>
					<div className="plan-info">
						<p className="plan-name">
							{ planName }
							{ plan.is_complimentary && (
								<span className="plan-badge">Cortesía</span>
							) }
						</p>
						{ expirationLabel && (
							<p className="plan-expiration">{ expirationLabel }</p>
						) }
					</div>
				</div>
				{ plan.notes && (
					<p className="plan-notes">{ plan.notes }</p>
				) }
			</div>
		);
	}

	renderAssignSection() {
		if (!this.state.showAssignSection) return null;

		const { assignForm, selectedChip, assigning } = this.state;
		const dateValue = assignForm.expiration_date
			? moment(assignForm.expiration_date, DATE_FORMAT).toDate()
			: null;

		return (
			<div className="assign-section">
				<div className="row">
					<div className="col-md-12">
						<Select
							color="white"
							name="plan_id"
							label="Plan"
							placeholder="Seleccionar plan"
							value={ assignForm.plan_id }
							onChange={ (e: any) => this.changeAssignField('plan_id', e.target.value) }
							options={ AVAILABLE_PLANS.map((p) => ({ value: String(p.id), label: p.name })) } />
					</div>

					<div className="col-md-12">
						<span className="chips-label">Duración</span>
						<div className="chips">
							{ DURATION_CHIPS.map((chip) => {
								const active = selectedChip === chip.key;
								return (
									<button
										key={ chip.key }
										type="button"
										onClick={ () => this.pickChip(chip) }
										className={ `chip ${ active ? 'chip-active' : '' }` }>
										{ chip.label }
									</button>
								);
							}) }
						</div>
						<DatePicker
							color="white"
							placeholder="Fecha de expiración"
							value={ dateValue }
							onChange={ (date: any) => this.changeExpirationDate(date) } />
					</div>

					<div className="col-md-12">
						<Checkbox
							label="Marcar como cortesía (no pagado)"
							name="is_complimentary"
							checked={ assignForm.is_complimentary }
							onChange={ (e: any) => this.changeAssignField('is_complimentary', e.target.checked) } />
					</div>

					<div className="col-md-12">
						<Textarea
							color="white"
							rows={ 3 }
							label="Nota / Razón (opcional)"
							placeholder="Máx. 500 caracteres"
							value={ assignForm.notes }
							name="notes"
							onChange={ (e: any) => this.changeAssignField('notes', e.target.value) } />
						<small className="notes-counter">
							{ assignForm.notes.length } / 500
						</small>
					</div>

					<div className="col-md-12">
						<button
							type="button"
							className="btn-confirm-assign"
							disabled={ assigning }
							onClick={ this.confirmAssign }>
							{ assigning ? 'Asignando...' : 'Confirmar asignación' }
						</button>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const hasActivePlan = !!this.state.currentPlan;
		const isOpen = this.state.showAssignSection;
		const toggleLabel = isOpen
			? 'Cancelar'
			: (hasActivePlan ? 'Cambiar plan' : 'Asignar plan');

		return (
			<div id="modal-update-user">
				<div className="row container-header">
					<div className="col-8">
						<p>Editar usuario</p>
					</div>
					<div className="col-4 text-right">
						{
							checkPermission(Constants.MODULES.USERS,'delete') && (
								<button type="button" className="btn btn-delete" onClick={ () => this.props.onDelete() }>
									<img src={ TrashIcon } />
								</button>
							)
						}
					</div>
				</div>

				<form onSubmit={ this.submit }>
					<div className="section-block">
						<p className="section-title">Datos del usuario</p>
						<div className="row">
							<div className="col-md-6">
								<Input
									color="white"
									value={ this.state.form.name }
									name="name"
									label="Nombre"
									onChange={ this.change } />
							</div>
							<div className="col-md-6">
								<Input
									color="white"
									value={ this.state.form.lastname }
									name="lastname"
									label="Apellido"
									onChange={ this.change } />
							</div>
							<div className="col-md-12">
								<Input
									color="white"
									value={ this.state.form.email }
									name="email"
									label="Correo"
									onChange={ this.change } />
							</div>
						</div>
					</div>

					<div className="section-block">
						<p className="section-title">Plan del usuario</p>
						{ this.renderCurrentPlan() }

						<div className="toggle-assign-row">
							<button
								type="button"
								className={ `toggle-assign ${ isOpen ? 'is-open' : '' }` }
								onClick={ this.toggleAssignSection }>
								{ toggleLabel }
							</button>
						</div>

						{ this.renderAssignSection() }
					</div>

					{ this.renderPasswordSection() }

					<div className="row modal-footer-row">
						<div className="col-md-6 text-center">
							<Button className="btn-close" type="button" onClick={ () => this.props.onClose() }>
								Cerrar
							</Button>
						</div>
						<div className="col-md-6 text-center">
							<Button className="btn-submit" type="submit">
								Guardar
							</Button>
						</div>
					</div>
				</form>
			</div>
		)
	}
}

export default ModalUpdate;
